import type { BulkUpdateImportedAccountsBody } from "@common/schemas/domains/accounting/importedAccount.schema";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import type { AccountType } from "@common/constants/domains/accounting/accounts";
import { FinancialAccountType } from "@common/types/financialAccount";

const FINANCIAL_MARKABLE_ACCOUNT_CLASSES = new Set<AccountClass>([
    AccountClass.assets,
    AccountClass.liabilities,
]);

interface AccountMappingBulkEligibilityFields {
    accountConnectionTaskId?: string | null;
    createNewAccount: boolean;
}

interface AccountMappingCreateNewAccountFields {
    id: string;
    kickType?: AccountType | null;
}

interface AccountMappingMarkAsFinancialFields {
    id: string;
    accountClass: AccountClass;
}

interface AccountMappingFinancialEligibilityFields extends AccountMappingBulkEligibilityFields {
    accountClass?: AccountClass;
    financialType?: FinancialAccountType | null;
    kickAccountId?: string | null;
    pendingAccountId?: string | null;
}

function isMappedForFinancialEligibility(
    account: Pick<
        AccountMappingFinancialEligibilityFields,
        "kickAccountId" | "pendingAccountId"
    >,
): boolean {
    return !!(account.kickAccountId ?? account.pendingAccountId);
}

function isSelectable(
    account: Pick<
        AccountMappingBulkEligibilityFields,
        "accountConnectionTaskId"
    >,
): boolean {
    return !account.accountConnectionTaskId;
}

function isCreateEligible(
    account: AccountMappingBulkEligibilityFields,
): boolean {
    return isSelectable(account) && !account.createNewAccount;
}

export function isMarkAsFinancialEligible(
    account: AccountMappingFinancialEligibilityFields,
): boolean {
    const { accountClass } = account;

    return (
        isCreateEligible(account) &&
        !account.financialType &&
        !isMappedForFinancialEligibility(account) &&
        accountClass != null &&
        FINANCIAL_MARKABLE_ACCOUNT_CLASSES.has(accountClass)
    );
}

export function isUnmarkAsFinancialEligible(
    account: Pick<
        AccountMappingFinancialEligibilityFields,
        | "accountConnectionTaskId"
        | "financialType"
        | "kickAccountId"
        | "pendingAccountId"
    >,
): boolean {
    return (
        isSelectable(account) &&
        account.financialType != null &&
        !isMappedForFinancialEligibility(account)
    );
}

// Uses kickType seeded at import (provider label → Kick group). Does not copy the
// mapped Kick account shown in the grid. User-edited kickType is preserved on
// repeat "Create account" unless they change it in the create-new row.
export function getCreateNewAccountUpdate(
    account: AccountMappingCreateNewAccountFields,
): BulkUpdateImportedAccountsBody["updates"][number] {
    return {
        importedAccountId: account.id,
        kickType: account.kickType ?? null,
        financialType: null,
        createNewAccount: true,
    };
}

export function getMapToAccountUpdate({
    id,
    accountId,
}: {
    id: string;
    accountId: string | null;
}): BulkUpdateImportedAccountsBody["updates"][number] {
    return {
        importedAccountId: id,
        accountId,
        ...(accountId ? { createNewAccount: false } : {}),
    };
}

export function getMarkAsFinancialUpdate(
    account: AccountMappingMarkAsFinancialFields,
): BulkUpdateImportedAccountsBody["updates"][number] {
    const financialType =
        account.accountClass === AccountClass.liabilities
            ? FinancialAccountType.CREDIT_CARD
            : FinancialAccountType.CHECKING;

    return {
        importedAccountId: account.id,
        financialType,
        accountId: null,
        createNewAccount: false,
    };
}

export function getUnmarkAsFinancialUpdate(
    importedAccountId: string,
): BulkUpdateImportedAccountsBody["updates"][number] {
    return {
        importedAccountId,
        financialType: null,
    };
}
